import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import AgentFeedback from '@/images/agents/AgentFeedback.svg';
import ClockCircle from '@/images/ClockCircle.svg';
import User from '@/images/User.svg';
import { useGetInteractionsAggregates } from '@/pages/agents/api/insights';
import { processInsightsAggregates } from '@/pages/agents/utils';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useAgentInsightsContext } from '../contex/useAgentInsightsContext';

const StyledSkeleton = styled(Skeleton)`
    transform: none;
    border-radius: 24px;
`;

const TopTableContainer = styled.div`
    display: flex;
    margin-bottom: 24px;
    gap: 16px;
    width: 100%;
`;

const TopTableCard = styled(Paper)`
    display: flex;
    flex-direction: row;
    padding: 24px;
    gap: 8px;
    flex: 1;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    box-shadow: none;
    min-height: 116px;
    justify-content: space-between;
`;

const CardContent = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const CardTitle = styled(Typography)`
    color: ${theme.colors.black[700]};
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
`;

const CardValue = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const CardNumber = styled(Typography)`
    color: ${theme.colors.secondary.main};
    font-family: Manrope;
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    line-height: 40px;
`;

const PercentageChange = styled(Typography)<{ change?: number }>`
    font-size: 16px;
    font-weight: 600;
    margin-top: 4px;
    color: ${({ theme, change }) => (change && change < 0 ? '#DA496D' : theme.colors.success[750])};
`;

const IconWrapper = styled(Box)`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
    border-radius: 8px;
    width: 48px;
    height: 48px;
`;

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${({ theme }) => theme.colors.primary.main};
`;

const SummaryTableSkeleton = () => {
    return (
        <TopTableContainer>
            {Array(3)
                .fill(0)
                .map((_, index) => (
                    <TopTableCard key={index} style={{ flexDirection: 'column', justifyContent: 'center' }}>
                        <StyledSkeleton variant="text" width={40} height={14} />
                        <StyledSkeleton variant="text" width={110} height={14} />
                    </TopTableCard>
                ))}
        </TopTableContainer>
    );
};

const formatTime = (seconds: number) => {
    if (seconds < 60) {
        return `${Math.round(seconds)} s`;
    } else {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} min`;
    }
};

export const SummaryTable = () => {
    const mixpanel = useAnalytics();
    const { dateRange, from, to, agent_ids, shareId, isPublic } = useAgentInsightsContext();

    const { data: total, isLoading } = useGetInteractionsAggregates({
        dateRange,
        from,
        to,
        agent_ids,
        shareId,
        isPublic,
        variables: ['session_time', 'n_user_actions', 'n_users', 'n_sessions'],
    });
    const processeSummary = processInsightsAggregates(total?.data || [])[0];

    useEffect(() => {
        if (total) {
            mixpanel?.track?.('agents-insights', {
                event: 'loaded',
                label: 'Insights',
            });
        }
    }, [total]);

    if (isLoading) {
        return <SummaryTableSkeleton />;
    }

    return (
        <TopTableContainer>
            <TopTableCard>
                <CardContent>
                    <CardTitle variant="body1">Users</CardTitle>
                    <CardValue>
                        <CardNumber>{processeSummary?.n_users || 0}</CardNumber>
                        {!!processeSummary?.users_change && (
                            <PercentageChange change={processeSummary?.users_change}>
                                {processeSummary?.users_change && processeSummary?.users_change < 0 ? '' : '+'}
                                {processeSummary?.users_change}%
                            </PercentageChange>
                        )}
                    </CardValue>
                </CardContent>
                <IconWrapper>
                    <StyledMaskedIcon src={User} size="24px" />
                </IconWrapper>
            </TopTableCard>
            <TopTableCard>
                <CardContent>
                    <CardTitle variant="body1">Conversations</CardTitle>
                    <CardValue>
                        <CardNumber>{processeSummary?.n_sessions || 0}</CardNumber>
                        {!!processeSummary?.conversations_change && (
                            <PercentageChange change={processeSummary?.conversations_change}>
                                {processeSummary?.conversations_change && processeSummary?.conversations_change < 0 ? '' : '+'}
                                {processeSummary?.conversations_change}%
                            </PercentageChange>
                        )}
                    </CardValue>
                </CardContent>
                <IconWrapper>
                    <StyledMaskedIcon src={AgentFeedback} size="24px" />
                </IconWrapper>
            </TopTableCard>
            <TopTableCard>
                <CardContent>
                    <CardTitle variant="body1">Average conversation time</CardTitle>
                    <CardValue>
                        <CardNumber>{formatTime(processeSummary?.session_time || 0)}</CardNumber>
                        {!!processeSummary?.session_time_change && (
                            <PercentageChange change={processeSummary?.session_time_change}>
                                {processeSummary?.session_time_change && processeSummary?.session_time_change < 0 ? '' : '+'}
                                {processeSummary?.session_time_change}%
                            </PercentageChange>
                        )}
                    </CardValue>
                </CardContent>
                <IconWrapper>
                    <StyledMaskedIcon src={ClockCircle} size="24px" />
                </IconWrapper>
            </TopTableCard>
        </TopTableContainer>
    );
};
